import { Injectable } from '@angular/core';
import { VerificationAction, IVerificationContext } from '@core/models';
import { getFriendlyCognitoMessage } from '@core/utils';
import { Observable, throwError, from, map, catchError, of } from 'rxjs';
import { CognitoAuthClientService } from './cognito-auth-client.service';
import { Logger } from '../logger';

const log = new Logger('VerificationResendService');

type OtpType = 'email' | 'phone' | undefined;
type CodeDetails = { DeliveryMedium?: string; Destination?: string };

@Injectable({ providedIn: 'root' })
export class VerificationResendService {
  private methodMap: Partial<
    Record<VerificationAction, (ctx: IVerificationContext) => Promise<any>>
  >;

  constructor(private cognitoAuthClient: CognitoAuthClientService) {
    this.methodMap = {
      signUp: async ctx => {
        if (!ctx.identifier) {
          throw new Error('Identifier required.');
        }

        try {
          return await this.cognitoAuthClient.resendSignUp(
            ctx.identifier,
            ctx.clientMetadata ?? { context: 'signUp' }
          );
        } catch (error: any) {
          // Enumeration safety: a non-existent (LEGACY pools only) or already-confirmed account must
          // be indistinguishable from a real resend. Cognito already simulates success for a
          // non-existent account under prevent_user_existence=ENABLED; resolve these the same way
          // (no delivery details) so the outcome, message, and form state match a genuine resend and
          // reveal nothing about the account. Genuine failures still propagate.
          if (
            error?.code === 'UserNotFoundException' ||
            error?.code === 'InvalidParameterException'
          ) {
            return {};
          }

          throw error;
        }
      },
      login: async ctx => {
        const session: any = ctx.session;
        if (!session || typeof session.resendConfirmationCode !== 'function') {
          throw new Error('Session missing or resendConfirmationCode unsupported.');
        }

        return session.resendConfirmationCode();
      },
      mfa: async () => {
        throw new Error('MFA resend not implemented.');
      }
    };
  }

  resendCode(ctx: IVerificationContext): Observable<{
    success: boolean;
    message: string;
    type: string;
    context?: IVerificationContext;
  }> {
    const handler = this.methodMap[ctx.actionType];

    if (!handler) {
      return throwError(() => new Error('Unsupported verification context.'));
    }

    return from(handler(ctx)).pipe(
      map((result: any) => {
        const codeDetails = this.extractDetails(result);
        const otpType = this.mapMediumToOtp(codeDetails.DeliveryMedium) ?? ctx.otpType;
        const destination = codeDetails.Destination ?? ctx.destination ?? '';

        log.debug('Resent code', {
          action: ctx.actionType,
          otpType: otpType,
          hasDestination: !!destination
        });

        const updated: IVerificationContext = {
          ...ctx,
          otpType: otpType,
          destination // kept only in-memory for UI masking
        };

        const messageByAction: Partial<Record<VerificationAction, string>> = {
          signUp: 'A new confirmation code has been sent.',
          login: 'A new MFA code has been sent.',
          mfa: 'A new MFA code has been sent.'
        };

        return {
          success: true,
          message: messageByAction[ctx.actionType] ?? 'A new code has been sent.',
          type: 'success',
          context: updated
        };
      }),
      catchError((error: any) => {
        log.error('Resend error', { name: error?.name, code: error?.code });
        const friendly = getFriendlyCognitoMessage('resendCode', error?.code);
        return of({ success: false, message: friendly.message, type: friendly.type });
      })
    );
  }

  private extractDetails(r: any): CodeDetails {
    return (
      r?.CodeDeliveryDetails ?? r?.codeDeliveryDetails ?? r?.CodeDeliveryDetailsList?.[0] ?? {}
    );
  }

  private mapMediumToOtp(m?: string): OtpType {
    const up = (m || '').toUpperCase();

    if (up === 'EMAIL') {
      return 'email';
    }

    if (up === 'SMS') {
      return 'phone';
    }

    return undefined;
  }
}
